/**
 * NotificationBell Component
 *
 * Header notification bell with urgent alert count and dropdown
 * TASK-164 Phase 2: Progressive Disclosure - Alert system
 */

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Bell, X, ChevronRight } from 'lucide-react';
import { DashboardAlert, PRIORITY_COLORS, getAlertIcon } from '../../../types/dashboard';
import { isAlertActive } from '../../../stores/alertStore';
import { primaryTriageCount, unresolvedGroups } from '../../../utils/triageCounts';
import { useAlertStore } from '../../../stores/alertStore';

interface NotificationBellProps {
  alerts: DashboardAlert[];
  maxPreview?: number;
  className?: string;
}

const NotificationBell: React.FC<NotificationBellProps> = ({
  alerts,
  maxPreview = 3,
  className = '',
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation('dashboard');
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // TASK-603: the popover previews the top GROUPS (cause · N clients), not the
  // top N raw alerts — 311 raw DNA rows collapse into one line.
  // TASK-653: the badge shows THE primary triage number — unresolved alert
  // groups — identical to Change Radar, the Alerts page, and Compliance.
  const groups = unresolvedGroups(alerts);
  const urgentGroups = groups
    .filter((g) => g.level === 'critical' || g.level === 'high')
    .slice(0, maxPreview);
  // Deep review #6: the badge shows the SERVER's count over every unresolved row; `groups`
  // below is the page-derived preview list, which is a different (and truncatable) thing.
  const counts = useAlertStore((s) => s.counts);
  const urgentCount = primaryTriageCount(alerts, counts);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleViewAll = () => {
    setIsOpen(false);
    navigate('/alerts');
  };

  const handleAlertClick = (alertId: string) => {
    setIsOpen(false);
    navigate(`/alerts?id=${alertId}`);
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors"
        aria-label={`${urgentCount} unresolved alert groups`}
      >
        <Bell className="w-5 h-5" />
        {urgentCount > 0 && (
          <span className="absolute top-0 right-0 flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-600 rounded-full">
            {urgentCount > 9 ? '9+' : urgentCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h3 className="text-sm font-semibold text-gray-900">Urgent Notifications</h3>
            <button
              onClick={() => setIsOpen(false)}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Grouped signal preview (TASK-603) */}
          {urgentGroups.length > 0 ? (
            <div className="max-h-96 overflow-y-auto">
              {urgentGroups.map((group) => (
                <button
                  key={group.key}
                  onClick={() => handleAlertClick(group.alerts[0].id)}
                  className="w-full p-4 border-b border-gray-100 hover:bg-gray-50 transition-colors text-left"
                >
                  <div className="flex items-start gap-3">
                    {/* Icon */}
                    <span className="text-lg flex-shrink-0 mt-0.5">
                      {getAlertIcon(group.level)}
                    </span>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span
                          className={`px-2 py-0.5 text-xs font-medium rounded ${
                            PRIORITY_COLORS[group.level].badge
                          } ${PRIORITY_COLORS[group.level].text}`}
                        >
                          {group.level.toUpperCase()}
                        </span>
                        <span className="text-xs text-gray-500 capitalize">
                          {group.category}
                        </span>
                      </div>
                      <h4 className="text-sm font-medium text-gray-900 mb-1 line-clamp-1">
                        {/* TASK-756: same keys the Alerts page and the Radar render, so one
                            cause reads identically wherever it appears. */}
                        {group.causeLabelKey ? t(group.causeLabelKey, group.causeLabel) : group.causeLabel}
                        <span className="ml-1 font-normal text-gray-500">
                          · {t('alerts.signals', {
                            count: group.alerts.length,
                            defaultValue: `${group.alerts.length} signals`,
                          })} ·{' '}
                          {t('alerts.clients', {
                            count: group.clients.length,
                            defaultValue: `${group.clients.length} clients`,
                          })}
                        </span>
                      </h4>
                      {group.sentence && (
                        <p className="text-xs text-gray-600 line-clamp-2 mb-1">
                          {group.sentenceKey ? t(group.sentenceKey, group.sentence) : group.sentence}
                        </p>
                      )}
                    </div>

                    {/* Arrow */}
                    <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0 mt-1" />
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center">
              <Bell className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500">No urgent notifications</p>
            </div>
          )}

          {/* Footer */}
          <div className="p-3 border-t border-gray-200">
            <button
              onClick={handleViewAll}
              className="w-full py-2 text-sm font-medium text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded transition-colors"
            >
              View all · {urgentCount} unresolved {urgentCount === 1 ? 'group' : 'groups'} (
              {alerts.filter(isAlertActive).length} signals)
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
